// Retry policy shared by the uploader and the API calls (B1, B10)
export class Retry {
  static BASE_DELAY_MS = 2_000;
  static MAX_DELAY_MS = 60_000;
  static JITTER = 0.25;

  // 2 s, 4 s, 8 s… ±25 %, never more than 60 s. The cap goes after the jitter
  // so no gap is longer than a minute.
  static backoffDelay(attempt) {
    const delay = Retry.BASE_DELAY_MS * 2 ** attempt;
    const jitter = 1 + (Math.random() * 2 - 1) * Retry.JITTER;

    return Math.round(Math.min(Retry.MAX_DELAY_MS, delay * jitter));
  }

  // No response (network error, abort, stall), 408, 429 and 5xx are worth
  // another try; any other status is not. The server's `retrievable` flag wins
  // when it is there.
  static isRetriable(error) {
    const retrievable = error?.response?.data?.retrievable;
    if (typeof retrievable === "boolean") {
      return retrievable;
    }

    const status = error?.response?.status;
    if (!status) {
      return true;
    }

    return status === 408 || status === 429 || status >= 500;
  }

  static wait(milliseconds) {
    return new Promise((resolve) => setTimeout(resolve, milliseconds));
  }

  // Calls `request` until it succeeds, fails for good, runs out of attempts or
  // reaches the deadline. The last wait is shortened to land on the deadline.
  // Rethrows the last error.
  static async withBackoff(request, {
    attempts = Infinity,
    deadlineMs = Infinity,
    delay = (attempt) => Retry.backoffDelay(attempt)
  } = {}) {
    const startedAt = Date.now();

    for (let attempt = 0; ; attempt++) {
      try {
        return await request();
      } catch (error) {
        const elapsed = Date.now() - startedAt;

        if (!Retry.isRetriable(error) || attempt + 1 >= attempts || elapsed >= deadlineMs) {
          throw error;
        }

        const milliseconds = Math.min(delay(attempt), deadlineMs - elapsed);
        console.warn(`Retry.withBackoff() attempt ${attempt + 1} failed, retrying in ${milliseconds} ms`, error);
        await Retry.wait(milliseconds);
      }
    }
  }
}
